import React, { useCallback, useMemo } from 'react';
import { Box, NotFound, Stack } from '@esgian/esgianui';
import BaselineKeyFigures from '@components/Sections/BaselineComparisonSection/SubSections/BaselineKeyFigures';
import PeriodicOverviewComparison from '@components/Sections/BaselineComparisonSection/SubSections/PeriodicOverview/PeriodicOverviewComparison';
import { useSelector } from 'react-redux';
import { getBaselineComparisonSlice } from '@store/features/filters/BaselineComparisonSlice/BaselineComparisonSlice';
import moment from 'moment-timezone';
import DrillingActivitiesComparison from '@components/Sections/BaselineComparisonSection/SubSections/DrillingActivities';
import EquipmentComparison from '@components/Sections/BaselineComparisonSection/SubSections/EquipmentComparison';
import { getMainPageSlice } from '@store/features/filters/MainPageSlice/MainPageSlice';
import AnalyticsComparison from '@components/Sections/BaselineComparisonSection/SubSections/AnalyticsComparison';
import PowerMgmtComparison from '@components/Sections/BaselineComparisonSection/SubSections/PowerMgmtComparison';
import { BASELINE_SUB_SECTIONS, BASELINE_TYPE, VALID_TARGET_BASELINE_SECTIONS } from '@constants/index';
import { getSideMenuOpen } from '@store/features';
import { useBaselineQueries } from '@hooks';

function BaselineComparisonSection() {
  const sideMenuOpen = useSelector(getSideMenuOpen);
  const { graphDisplays, selectedBaseline } = useSelector(getBaselineComparisonSlice);
  const { selectedRig, startDate, endDate } = useSelector(getMainPageSlice);
  const { baselineEquipmentListQueries } = useBaselineQueries();

  const validFilters = useMemo(() => {
    return !(
      !selectedRig ||
      !selectedBaseline ||
      !startDate ||
      !endDate ||
      !moment(startDate).isValid() ||
      !moment(endDate).isValid()
    );
  }, [selectedRig, selectedBaseline, startDate, endDate]);

  const getKey = useCallback((graphName) => {
    return `section-${graphName.replace(/\s+/g, '-')}`;
  }, []);

  const sections = useMemo(() => {
    if (selectedBaseline && selectedBaseline.baselineTypeId === BASELINE_TYPE.TARGET) {
      return graphDisplays?.filter(
        ({ visible, graphName }) => visible && VALID_TARGET_BASELINE_SECTIONS.includes(graphName)
      );
    }
    return graphDisplays?.filter(({ visible }) => visible);
  }, [selectedBaseline, graphDisplays]);

  const mainEquipmentList = baselineEquipmentListQueries[0].data;
  const baselineEquipmentList = baselineEquipmentListQueries[1].data;

  const allEquipments = useMemo(() => {
    if (!mainEquipmentList?.length || !baselineEquipmentList?.length) return [];
    const equipmentNames = new Map([...mainEquipmentList, ...baselineEquipmentList].map(e => ({ 
      order: e.chartConfig.order, 
      equipmentName: e.equipmentName, 
      equipmentType: e.equipmentType, 
      displayName: e.chartConfig.displayName 
    }))
    .map((r) => [r.equipmentName, r]))
    .values();
    
    return Array.from(equipmentNames).sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));
  }, [mainEquipmentList, baselineEquipmentList]);

  if (!validFilters) {
    return (
      <Box sx={{ pt: '5em' }}>
        <NotFound iconSize={'7em'} header={'No data'} show text={'Please update filters'} />
      </Box>
    );
  }

  return (
    <Stack
      spacing={2}
      sx={{ maxWidth: sideMenuOpen ? 'calc(100vw - 316px)' : 'calc(100vw - 64px)' }}>
      <BaselineKeyFigures />
      {sections?.map(({ graphName }) => {
        switch (graphName) {
          case BASELINE_SUB_SECTIONS.PERIODIC_OVERVIEW:
            return <PeriodicOverviewComparison key={getKey(graphName)} allEquipments={allEquipments} />;
          case BASELINE_SUB_SECTIONS.DRILLING_ACTIVITIES:
            return <DrillingActivitiesComparison key={getKey(graphName)} />;
          case BASELINE_SUB_SECTIONS.EQUIPMENT:
            return <EquipmentComparison key={getKey(graphName)} allEquipments={allEquipments} />;
          case BASELINE_SUB_SECTIONS.POWER_MGMT:
            return <PowerMgmtComparison key={getKey(graphName)} />;
          case BASELINE_SUB_SECTIONS.ANALYTICS:
            return <AnalyticsComparison key={getKey(graphName)} />;
          default:
            break;
        }
      })}
    </Stack>
  );
}

BaselineComparisonSection.propTypes = {};

BaselineComparisonSection.defaultProps = {};

export default BaselineComparisonSection;
